import { DateTime } from "luxon";
import pluralize from "pluralize";

export interface DateFormatOptions {
  inputZone?: string;
  outputZone?: string;
}

/**
 * Prints a date in a medium format. "Jan 1, 2022"
 *
 * @param isoDateStr - The date string to print.
 * @param options - The options for the date format.
 * @returns The formatted date string.
 */
export function prettyPrintDateMed(
  isoDateStr: string,
  options?: DateFormatOptions
) {
  const dt = getDateFromString(isoDateStr, options);
  return dt.isValid ? dt.toLocaleString(DateTime.DATE_MED) : "Invalid DateTime";
}

/**
 * Prints a date in a month and year format. "Jan 2022"
 *
 * @param isoDateStr - The date string to print.
 * @param options - The options for the date format.
 * @returns The formatted date string.
 */
export function prettyPrintMonthYear(
  isoDateStr: string,
  options?: DateFormatOptions
) {
  const dt = getDateFromString(isoDateStr, options);
  return dt.isValid
    ? dt.toLocaleString({ month: "short", year: "numeric" })
    : "Invalid DateTime";
}

export function prettyPrintDateTimeMed(
  isoDateStr: string,
  options?: DateFormatOptions
) {
  const { inputZone = "utc", outputZone } = options || {};
  let dt = DateTime.fromISO(isoDateStr, { zone: inputZone });
  if (outputZone !== undefined) {
    dt = dt.setZone(outputZone);
  } else {
    // Convert to local timezone when outputZone is not specified
    dt = dt.setZone();
  }
  return dt.isValid
    ? dt.toLocaleString(DateTime.DATETIME_MED)
    : "Invalid DateTime";
}

/**
 * Prints a date in a full format. "January 1, 2022"
 *
 * @param isoDateStr - The date string to print.
 * @param options - The options for the date format.
 * @returns The formatted date string.
 */
export function prettyPrintDateFull(
  isoDateStr: string,
  options?: DateFormatOptions
) {
  const dt = getDateFromString(isoDateStr, options);
  return dt.isValid
    ? dt.toLocaleString(DateTime.DATE_FULL)
    : "Invalid DateTime";
}

/**
 * Prints a date in a short format. "01/01/2022"
 *
 * @param isoDateStr - The date string to print.
 * @param options - The options for the date format.
 * @returns The formatted date string.
 */
export function prettyPrintDate(
  isoDateStr: string,
  options?: DateFormatOptions
) {
  const dt = getDateFromString(isoDateStr, options);
  return dt.isValid ? dt.toFormat("LL/dd/yyyy") : "Invalid DateTime";
}

export function prettyPrintDateTime(
  isoDateStr: string,
  options?: DateFormatOptions
) {
  const { inputZone = "utc", outputZone } = options || {};
  let dt = DateTime.fromISO(isoDateStr, { zone: inputZone });
  if (outputZone !== undefined) {
    dt = dt.setZone(outputZone);
  } else {
    // Convert to local timezone when outputZone is not specified
    dt = dt.setZone();
  }
  return dt.isValid ? dt.toFormat("MM/dd/yyyy hh:mm a") : "Invalid DateTime";
}

export function prettyPrintDateTimeWithSeconds(
  isoDateStr: string,
  options?: DateFormatOptions
) {
  const { inputZone = "utc", outputZone } = options || {};
  let dt = DateTime.fromISO(isoDateStr, { zone: inputZone });
  if (outputZone !== undefined) {
    dt = dt.setZone(outputZone);
  } else {
    // Convert to local timezone when outputZone is not specified
    dt = dt.setZone();
  }
  return dt.isValid ? dt.toFormat("MM/dd/yyyy hh:mm:ss a") : "Invalid DateTime";
}

/**
 * Pretty prints a measurement's start/end in a compact, human-friendly form:
 * - Full month span (e.g., 2025-10-01 to 2025-10-31) => "Oct 2025"
 * - Full quarter span (e.g., 2025-10-01 to 2025-12-31) => "Q4 2025"
 * - Otherwise => medium date for start, e.g., "Oct 1, 2025"
 */
export function prettyPrintMeasurementRange(
  startIso: string,
  endIso?: string
): string {
  const dtStart = DateTime.fromISO(startIso, { zone: "utc" });
  if (!endIso) {
    return dtStart.isValid
      ? dtStart.toLocaleString(DateTime.DATE_MED)
      : "Invalid DateTime";
  }
  const dtEnd = DateTime.fromISO(endIso, { zone: "utc" });
  if (!dtStart.isValid || !dtEnd.isValid) {
    return prettyPrintDateMed(startIso);
  }

  const isFirstOfMonth = dtStart.day === 1;
  const isLastOfMonth = dtEnd.endOf("month").day === dtEnd.day;
  const sameMonthYear =
    dtStart.year === dtEnd.year && dtStart.month === dtEnd.month;

  // Month span
  if (isFirstOfMonth && isLastOfMonth && sameMonthYear) {
    return dtStart.toLocaleString({ month: "short", year: "numeric" });
  }

  // Quarter span
  const quarterStartMonths = [1, 4, 7, 10]; // Jan, Apr, Jul, Oct
  const isQuarterStart =
    quarterStartMonths.includes(dtStart.month) && isFirstOfMonth;
  const expectedQuarterEndMonth = dtStart.month + 2; // end month within same year
  const isQuarterEnd =
    dtEnd.year === dtStart.year &&
    dtEnd.month === expectedQuarterEndMonth &&
    dtEnd.endOf("month").day === dtEnd.day;

  if (isQuarterStart && isQuarterEnd) {
    const q = Math.floor((dtStart.month - 1) / 3) + 1;
    return `Q${q} ${dtStart.year}`;
  }

  // Generic span formatting:
  // - same year: "Oct 2 - Oct 31, 2025"
  // - different years: "Dec 15, 2025 - Jan 14, 2026"
  if (dtStart.year === dtEnd.year) {
    const left = dtStart.toFormat("LLL d");
    const right = dtEnd.toFormat("LLL d");
    return `${left} - ${right}, ${dtStart.year}`;
  }
  const left = dtStart.toFormat("LLL d, yyyy");
  const right = dtEnd.toFormat("LLL d, yyyy");
  return `${left} - ${right}`;
}

/**
 * Returns the ISO date string for the last day of the month for the given ISO date.
 */
export function getEndOfMonthIso(isoDate: string): string | undefined {
  const dt = DateTime.fromISO(isoDate, { zone: "utc" });
  if (!dt.isValid) return undefined;
  return dt.endOf("month").toISODate() ?? undefined;
}

/**
 * Returns the ISO date string for the last day of the quarter for the given ISO date.
 * Quarter starts: Jan (1), Apr (4), Jul (7), Oct (10)
 */
export function getEndOfQuarterIso(isoDate: string): string | undefined {
  const dt = DateTime.fromISO(isoDate, { zone: "utc" });
  if (!dt.isValid) return undefined;
  const startMonth = [1, 4, 7, 10][Math.floor((dt.month - 1) / 3)];
  const endMonth = startMonth + 2;
  const end = DateTime.fromObject(
    { year: dt.year, month: endMonth, day: 1 },
    { zone: "utc" }
  ).endOf("month");
  return end.toISODate() ?? undefined;
}
/**
 * Relative time is useful for logs, but after about a week it's better to see
 * the full date time.
 * After relativeCutoffHours, the date will be formatted as a full date -- defaults to a week.
 */
export function relativeLogTime(
  isoDateStr: string,
  relativeCutoffHours = 24 * 7,
  options?: DateFormatOptions
) {
  const { outputZone } = options || {};
  let dateTime = DateTime.fromISO(isoDateStr);
  if (outputZone !== undefined) {
    dateTime = dateTime.setZone(outputZone);
  } else {
    // Convert to local timezone when outputZone is not specified
    dateTime = dateTime.setZone();
  }
  const now =
    outputZone !== undefined
      ? DateTime.now().setZone(outputZone)
      : DateTime.now();
  const relativeCutoffMoment = now.minus({ hours: relativeCutoffHours });

  // Check if the date is more than a year ago
  if (dateTime < relativeCutoffMoment) {
    // Format it in a specific way, e.g., 'MM/dd/yyyy'
    return prettyPrintDateTime(isoDateStr, { outputZone });
  } else {
    // Use relative formatting
    return dateTime.toRelative({ base: now }) || "";
  }
}

/**
 * Gets a date from a string.
 *
 * If the string does not contain a timezone (e.g. "2022-01-01"), it will be
 * considered UTC and NOT be converted to a local timezone.
 *
 * If the string contains a timezone, it will be used and converted to the
 * output zone (local timezone if not specified).
 *
 * @param options
 * @param isoDateStr
 * @returns
 */
function getDateFromString(
  isoDateStr: string,
  options: DateFormatOptions | undefined
) {
  const { inputZone, outputZone } = options || {};
  const dt = DateTime.fromISO(isoDateStr, { zone: inputZone }).setZone(
    outputZone
  );
  return dt;
}

export function prettyPrintDaysFromInt(days: number): string {
  switch (days) {
    case 0:
      return `<1 day`;
    default:
      return `${days} ${pluralize("day", days)}`;
  }
}
